<template>
  <Dialog
    v-model:visible="visible"
    header="Search SATS"
    modal
    dismissableMask
    blockScroll
    position="top"
    appendTo="body"
    :draggable="false"
    :breakpoints="dialogBreakpoints"
    :style="{ width: '48rem' }"
    :pt="dialogPassThrough"
    @hide="resetSearch"
  >
    <div class="p-3 md:p-4 bg-white">
      <p class="site-search-intro mt-0 mb-3">
        Find programmes, faculty, articles, courses, and other SATS resources.
      </p>

      <div class="flex align-items-center gap-2">
        <IconField iconPosition="left" class="flex-1">
          <InputIcon class="site-search-input-icon pi pi-search" aria-hidden="true" />
          <InputText
            v-model="searchQuery"
            type="search"
            placeholder="What are you looking for?"
            aria-label="Search the SATS website"
            aria-describedby="site-search-status"
            autocomplete="off"
            :maxlength="200"
            autofocus
            class="site-search-input w-full pr-3 pl-6 bg-white border-1 border-round-xl"
          />
        </IconField>

        <Button
          v-if="searchQuery"
          type="button"
          icon="pi pi-times"
          text
          rounded
          aria-label="Clear search"
          class="site-search-clear-button flex-shrink-0 w-3rem h-3rem border-none"
          @click="searchQuery = ''"
        />
      </div>

      <p
        id="site-search-status"
        class="site-search-status mt-2 mb-0"
        role="status"
        aria-live="polite"
      >
        {{ searchStatus }}
      </p>
    </div>

    <Divider class="site-search-divider m-0" />

    <Card
      v-if="searchUnavailable"
      class="site-search-state-card border-none border-noround shadow-none bg-transparent"
      :pt="stateCardPassThrough"
    >
      <template #content>
        <div
          class="site-search-state flex flex-column align-items-center justify-content-center text-center p-5"
        >
          <Avatar icon="pi pi-exclamation-circle" shape="circle" class="site-search-state-icon" />
          <h2 class="site-search-state-title mt-3 mb-1 font-bold">
            Search is temporarily unavailable
          </h2>
          <p class="site-search-state-copy mt-0 mb-3 max-w-28rem line-height-3">
            Please try again.
          </p>
          <Button label="Try again" icon="pi pi-refresh" outlined @click="retrySearch" />
        </div>
      </template>
    </Card>

    <Card
      v-else-if="!canSearch"
      class="site-search-state-card border-none border-noround shadow-none bg-transparent"
      :pt="stateCardPassThrough"
    >
      <template #content>
        <div
          class="site-search-state flex flex-column align-items-center justify-content-center text-center p-5"
        >
          <Avatar icon="pi pi-search" shape="circle" class="site-search-state-icon" />
          <h2 class="site-search-state-title mt-3 mb-1 font-bold">Search the SATS website</h2>
          <p class="site-search-state-copy m-0 max-w-28rem line-height-3">
            Type at least three characters to get started.
          </p>
        </div>
      </template>
    </Card>

    <Card
      v-else-if="searchPending"
      class="site-search-state-card border-none border-noround shadow-none bg-transparent"
      :pt="stateCardPassThrough"
    >
      <template #content>
        <div class="site-search-state flex flex-column align-items-center justify-content-center text-center p-5">
          <Avatar icon="pi pi-spin pi-spinner" shape="circle" class="site-search-state-icon" aria-hidden="true" />
          <h2 class="site-search-state-title mt-3 mb-1 font-bold">Searching…</h2>
        </div>
      </template>
    </Card>

    <ScrollPanel
      v-else-if="groupedResults.length"
      class="site-search-results w-full"
      :pt="scrollPanelPassThrough"
    >
      <div class="flex flex-column gap-3 p-3 md:p-4">
        <section
          v-for="group in groupedResults"
          :key="group.key"
          :aria-labelledby="`site-search-group-${group.key}`"
        >
          <Card
            class="site-search-group-card shadow-none overflow-hidden border-1"
            :pt="groupCardPassThrough"
          >
            <template #title>
              <div class="flex align-items-center justify-content-between gap-3">
                <div class="flex align-items-center gap-2 min-w-0">
                  <Avatar
                    :icon="group.icon"
                    shape="circle"
                    class="site-search-group-icon w-2rem h-2rem"
                  />
                  <h2
                    :id="`site-search-group-${group.key}`"
                    class="site-search-group-title m-0 overflow-hidden text-overflow-ellipsis white-space-nowrap font-bold"
                  >
                    {{ group.label }}
                  </h2>
                </div>
                <Badge
                  :value="group.results.length"
                  class="site-search-count flex-shrink-0"
                  :aria-label="`${group.results.length} results in ${group.label}`"
                />
              </div>
            </template>

            <template #content>
              <ul class="site-search-result-list list-none p-0 m-0">
                <li v-for="result in group.results" :key="result.href">
                  <NuxtLink
                    :to="result.href"
                    class="site-search-result flex align-items-start gap-3 p-3 no-underline"
                    @click="closeSearch"
                  >
                    <Avatar
                      :icon="group.icon"
                      shape="circle"
                      class="site-search-result-icon flex-shrink-0"
                      aria-hidden="true"
                    />

                    <span class="flex-1 min-w-0">
                      <strong class="site-search-result-title block">{{ result.title }}</strong>
                      <span v-if="result.snippet" class="site-search-result-snippet block mt-1">
                        {{ result.snippet }}
                      </span>
                      <span
                        class="site-search-result-path flex align-items-center gap-1 mt-2 overflow-hidden text-overflow-ellipsis white-space-nowrap"
                      >
                        <i class="pi pi-folder-open" aria-hidden="true"></i>
                        <span class="overflow-hidden text-overflow-ellipsis">
                          {{ result.breadcrumb }}
                        </span>
                      </span>
                    </span>

                    <i
                      class="site-search-result-arrow pi pi-arrow-right flex-shrink-0 mt-1"
                      aria-hidden="true"
                    ></i>
                  </NuxtLink>
                </li>
              </ul>
            </template>
          </Card>
        </section>
      </div>
    </ScrollPanel>

    <Card
      v-else
      class="site-search-state-card border-none border-noround shadow-none bg-transparent"
      :pt="stateCardPassThrough"
    >
      <template #content>
        <div
          class="site-search-state flex flex-column align-items-center justify-content-center text-center p-5"
        >
          <Avatar icon="pi pi-search-minus" shape="circle" class="site-search-state-icon" />
          <h2 class="site-search-state-title mt-3 mb-1 font-bold">
            No results for “{{ normalizedQuery }}”
          </h2>
          <p class="site-search-state-copy m-0 max-w-28rem line-height-3">
            Check the spelling or try a broader search term.
          </p>
        </div>
      </template>
    </Card>
  </Dialog>
</template>

<script setup>

const MINIMUM_SEARCH_LENGTH = 3
const visible = defineModel('visible', { type: Boolean, default: false })
const searchQuery = ref('')
const searchUnavailable = ref(false)
const searchPending = ref(false)
const searchAttempt = ref(0)
/** @type {import('vue').Ref<import('../utils/searchResults').SearchResponse>} */
const searchResponse = ref({ groups: [], hasMore: false })
const normalizedQuery = computed(() => searchQuery.value.trim())
const canSearch = computed(() => normalizedQuery.value.length >= MINIMUM_SEARCH_LENGTH)

watch([normalizedQuery, visible, searchAttempt], ([query, isVisible], _previous, onCleanup) => {
  searchResponse.value = { groups: [], hasMore: false }
  searchUnavailable.value = false
  searchPending.value = false
  if (!isVisible || query.length < MINIMUM_SEARCH_LENGTH) return

  const controller = new AbortController()
  searchPending.value = true
  const timer = setTimeout(async () => {
    try {
      const response = await $fetch('/api/search', {
        query: { q: query },
        signal: controller.signal,
        retry: 0,
      })
      if (!controller.signal.aborted) searchResponse.value = response
    } catch (error) {
      if (!controller.signal.aborted) {
        searchUnavailable.value = true
        console.error('Unable to search the website.', error)
      }
    } finally {
      if (!controller.signal.aborted) searchPending.value = false
    }
  }, 250)

  onCleanup(() => {
    clearTimeout(timer)
    controller.abort()
  })
}, { immediate: true })

const dialogBreakpoints = {
  '960px': '80vw',
  '640px': 'calc(100vw - 1rem)',
}

const dialogPassThrough = {
  mask: { class: 'site-search-mask align-items-start' },
  root: {
    class: 'site-search-dialog relative overflow-hidden bg-white border-1 border-round-2xl',
  },
  header: { class: 'site-search-dialog-header border-none px-3 md:px-4 pt-4 pb-3' },
  title: { class: 'site-search-dialog-title font-bold' },
  closeButton: { class: 'site-search-close-button' },
  content: { class: 'site-search-dialog-content p-0' },
}

const stateCardPassThrough = {
  body: { class: 'p-0' },
  content: { class: 'p-0' },
}

const groupCardPassThrough = {
  body: { class: 'p-0' },
  caption: { class: 'm-0' },
  title: { class: 'm-0 p-3 border-bottom-1' },
  content: { class: 'p-0' },
}

const scrollPanelPassThrough = {
  content: { class: 'p-0' },
  barY: { class: 'site-search-scrollbar' },
  barX: { class: 'hidden' },
}

const groupedResults = computed(() => searchResponse.value.groups)
const resultCount = computed(() => groupedResults.value.reduce((total, group) => total + group.results.length, 0))

const searchStatus = computed(() => {
  if (searchUnavailable.value) return 'Search is temporarily unavailable.'
  if (!canSearch.value) return 'Type at least three characters to search.'
  if (searchPending.value) return 'Searching…'
  if (!resultCount.value) return `No results found for ${normalizedQuery.value}.`
  if (searchResponse.value.hasMore) return `Showing the top ${resultCount.value} results.`
  return `${resultCount.value} result${resultCount.value === 1 ? '' : 's'} found.`
})

function closeSearch() {
  visible.value = false
}

function resetSearch() {
  searchQuery.value = ''
}

function retrySearch() {
  searchAttempt.value += 1
}
</script>

<style lang="scss">
.site-search-mask.p-dialog-mask {
  padding: clamp(1rem, 8vh, 5rem) 1rem 1rem;
}

.site-search-dialog.p-dialog {
  max-height: calc(100dvh - 2rem);
  color: var(--sats-ink);
  border-color: var(--sats-border);
  box-shadow: 0 1.25rem 3.5rem rgba(17, 48, 38, 0.22);
}

.site-search-dialog.p-dialog::before {
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  left: 0;
  height: 0.25rem;
  content: '';
  background: linear-gradient(
    90deg,
    var(--sats-green) 0 70%,
    var(--sats-gold) 70% 85%,
    var(--sats-purple) 85% 100%
  );
}

.site-search-dialog-header.p-dialog-header {
  color: var(--sats-ink);
  background: linear-gradient(
    135deg,
    rgba(var(--sats-green-rgb), 0.095),
    rgba(255, 255, 255, 0.98)
  );
}

.site-search-dialog-title.p-dialog-title {
  color: var(--sats-ink);
  font-size: 1.35rem;
  letter-spacing: -0.015em;
}

.site-search-close-button.p-dialog-header-icon {
  color: var(--sats-green);
}

.site-search-close-button.p-dialog-header-icon:hover {
  color: #ffffff;
  background: var(--sats-green);
}

.site-search-close-button.p-dialog-header-icon:focus-visible,
.site-search-clear-button.p-button:focus-visible {
  outline: 0.18rem solid var(--sats-focus-outline);
  outline-offset: 0.12rem;
  box-shadow: none;
}

.site-search-dialog-content.p-dialog-content {
  color: var(--sats-ink);
  background: var(--sats-surface-soft);
}

.site-search-intro,
.site-search-status,
.site-search-state-copy,
.site-search-result-snippet,
.site-search-result-path {
  color: var(--sats-muted);
}

.site-search-intro {
  font-size: 0.9rem;
  line-height: 1.45;
}

.site-search-status {
  min-height: 1.2rem;
  font-size: 0.78rem;
  line-height: 1.4;
}

.site-search-input.p-inputtext {
  height: 3.15rem;
  color: var(--sats-ink);
  border-color: var(--sats-control-border);
  box-shadow: none;
}

.site-search-input.p-inputtext::-webkit-search-cancel-button {
  display: none;
}

.site-search-input.p-inputtext:enabled:hover {
  border-color: rgba(var(--sats-green-rgb), 0.55);
}

.site-search-input.p-inputtext:enabled:focus {
  border-color: var(--sats-green);
  box-shadow: 0 0 0 0.2rem rgba(var(--sats-green-rgb), 0.13);
}

.site-search-input-icon.p-input-icon {
  left: 1rem;
  color: var(--sats-green);
}

.site-search-clear-button.p-button.p-button-text {
  color: var(--sats-green);
  background: rgba(var(--sats-green-rgb), 0.07);
}

.site-search-clear-button.p-button.p-button-text:hover {
  color: #ffffff;
  background: var(--sats-green);
}

.site-search-divider.p-divider-horizontal::before {
  border-top-color: var(--sats-border);
}

.site-search-state-card.p-card {
  color: var(--sats-ink);
}

.site-search-state {
  min-height: 15rem;
}

.site-search-state-icon.p-avatar {
  width: 3.5rem;
  height: 3.5rem;
  color: var(--sats-green);
  background: var(--sats-green-soft);
}

.site-search-state-icon .p-avatar-icon {
  font-size: 1.4rem;
}

.site-search-state-title {
  color: var(--sats-ink);
  font-size: 1.05rem;
}

.site-search-state-copy {
  font-size: 0.88rem;
}

.site-search-results.p-scrollpanel {
  height: min(56dvh, 34rem);
}

.site-search-scrollbar.p-scrollpanel-bar {
  width: 0.35rem;
  background: rgba(var(--sats-green-rgb), 0.25);
  border-radius: 999px;
}

.site-search-group-card.p-card {
  color: var(--sats-ink);
  background: rgba(255, 255, 255, 0.94);
  border-color: var(--sats-border);
  border-radius: 0.85rem;
}

.site-search-group-card .p-card-title {
  background: linear-gradient(
    135deg,
    rgba(var(--sats-green-rgb), 0.075),
    rgba(255, 255, 255, 0.96)
  );
  border-bottom-color: var(--sats-border);
}

.site-search-group-icon.p-avatar,
.site-search-result-icon.p-avatar {
  color: var(--sats-green);
  background: var(--sats-green-soft);
}

.site-search-result-icon.p-avatar {
  width: 2.35rem;
  height: 2.35rem;
}

.site-search-group-title {
  color: var(--sats-ink);
  font-size: 0.92rem;
}

.site-search-count.p-badge {
  min-width: 1.65rem;
  height: 1.65rem;
  color: var(--sats-green-dark);
  background: rgba(var(--sats-gold-rgb), 0.2);
  border-radius: 999px;
}

.site-search-result-list > li + li {
  border-top: 1px solid var(--sats-border);
}

.site-search-result {
  min-height: 4.75rem;
  color: var(--sats-ink);
  transition:
    color 0.18s ease,
    background-color 0.18s ease;
}

.site-search-result:hover {
  color: var(--sats-green-dark);
  background: rgba(var(--sats-green-rgb), 0.055);
}

.site-search-result:focus-visible {
  position: relative;
  z-index: 1;
  outline: 0.18rem solid rgba(var(--sats-gold-rgb), 0.7);
  outline-offset: -0.18rem;
}

.site-search-result-title {
  overflow-wrap: anywhere;
  color: inherit;
  font-size: 0.94rem;
  line-height: 1.35;
}

.site-search-result-snippet {
  display: -webkit-box;
  overflow: hidden;
  font-size: 0.8rem;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.site-search-result-path {
  font-size: 0.72rem;
  line-height: 1.3;
}

.site-search-result-path i {
  color: var(--sats-gold);
  font-size: 0.7rem;
}

.site-search-result-arrow {
  color: var(--sats-gold);
  font-size: 0.85rem;
  transition: transform 0.18s ease;
}

.site-search-result:hover .site-search-result-arrow {
  transform: translateX(0.18rem);
}

@media (max-width: 640px) {
  .site-search-mask.p-dialog-mask {
    padding: 0.5rem;
  }

  .site-search-dialog.p-dialog {
    max-height: calc(100dvh - 1rem);
  }

  .site-search-results.p-scrollpanel {
    height: calc(100dvh - 15.5rem);
    min-height: 0;
  }

  .site-search-result {
    padding: 0.85rem !important;
  }

  .site-search-result-snippet {
    -webkit-line-clamp: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .site-search-result,
  .site-search-result-arrow {
    transition: none;
  }
}
</style>
